// src/components/weeklyReport/ReviewSubmitModal.js
//
// The last step: every study's numbers, filled in from the people and
// follow-up numbers the coordinator added (read-only), the pre-screens not tied
// to a study, and the Submit button. Also the "you haven't
// submitted yet" warning shown when leaving the page.

import { Send, Loader2, ClipboardList, AlertTriangle } from "lucide-react";
import { WrModal, BTN_PRIMARY, BTN_SOFT } from "./ui";
import {
  REPORT_STATUSES,
  studyTotals,
  reportTotals,
  studiesToSubmit,
  countValue,
} from "../../services/weeklyReportModel";

export const ReviewSubmitModal = ({
  open,
  studies,
  counts,
  coordinator,
  segment,
  canSubmit,
  blockedReason,
  submitting,
  error,
  onSubmit,
  onClose,
}) => {
  const included = studiesToSubmit({ studies });
  const empty = studies.filter((s) => !included.includes(s));
  const totals = reportTotals(included);
  const preScreensNotTied = countValue(counts?.preScreens);
  const th = "px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400 text-right";
  const td = "px-2 py-1.5 text-sm text-white text-right tabular-nums";

  return (
    <WrModal
      open={open}
      onClose={onClose}
      wide
      icon={ClipboardList}
      title="Review your weekly report"
      subtitle={`${coordinator || "No coordinator"} · ${segment ? `${segment.label} (${segment.monthLabel})` : ""}`}
      footer={
        <>
          <button type="button" onClick={onClose} className={`${BTN_SOFT} mr-auto`} disabled={submitting}>
            Back to edit
          </button>
          <button type="button" onClick={onSubmit} className={BTN_PRIMARY} disabled={!canSubmit || submitting}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? "Submitting…" : "Submit report"}
          </button>
        </>
      }
    >
      {included.length === 0 && preScreensNotTied === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">
          Add a study (people or a follow-up number), or pre-screens, before submitting.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className={`${th} text-left`}>Study</th>
                {REPORT_STATUSES.map((s) => (
                  <th key={s.id} className={th}>
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {included.map((s) => {
                const t = studyTotals(s);
                return (
                  <tr key={s.studyDocId} className="border-b border-white/5">
                    <td className="px-2 py-1.5 text-sm text-white">
                      {s.studyId ? `${s.studyId} – ` : ""}
                      {s.name}
                    </td>
                    {REPORT_STATUSES.map((st) => (
                      <td key={st.id} className={td}>
                        {t[st.field]}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {preScreensNotTied > 0 && (
                <tr className="border-b border-white/5">
                  <td className="px-2 py-1.5 text-sm text-slate-300 italic">No study</td>
                  {REPORT_STATUSES.map((st) => (
                    <td key={st.id} className={td}>
                      {st.id === "preScreen" ? preScreensNotTied : <span className="text-slate-500">—</span>}
                    </td>
                  ))}
                </tr>
              )}
              <tr>
                <td className="px-2 py-2 text-sm font-bold text-white">Total</td>
                {REPORT_STATUSES.map((st) => (
                  <td key={st.id} className={`${td} font-bold`}>
                    {totals[st.field] + (st.id === "preScreen" ? preScreensNotTied : 0)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-[11px] text-slate-400">
        Filled in from what you added. To change a number, go back and edit it.
      </p>
      {empty.length > 0 && (
        <p className="weekly-report-warn mt-3 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
          {empty.map((s) => s.name || s.studyId).join(", ")} {empty.length === 1 ? "has" : "have"} nothing added and
          won't be included.
        </p>
      )}
      {blockedReason && (
        <p className="weekly-report-error mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
          {blockedReason}
        </p>
      )}
      {error && (
        <p className="weekly-report-error mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
          {error}
        </p>
      )}
    </WrModal>
  );
};

export const LeaveGuardModal = ({ open, saveFailed, onStay, onLeave }) => (
  <WrModal
    open={open}
    onClose={onStay}
    icon={AlertTriangle}
    title="Your report isn't submitted yet"
    footer={
      <>
        <button type="button" onClick={onLeave} className={`${BTN_SOFT} mr-auto`}>
          Leave anyway
        </button>
        <button type="button" onClick={onStay} className={BTN_PRIMARY}>
          Keep working
        </button>
      </>
    }
  >
    {saveFailed ? (
      <p className="text-sm text-rose-200 weekly-report-error-text">
        Your latest changes couldn't be saved (check your connection). If you leave now, they may be lost.
      </p>
    ) : (
      <p className="text-sm text-slate-300">
        Your progress is saved as a draft on your account, so it will be here when you come back. It won't reach your
        admin until you submit it.
      </p>
    )}
  </WrModal>
);
